import React from 'react';
import classnames from 'classnames';
import { useSwipeable } from 'react-swipeable';

import OCButton from '../../../../../button';
import Icon from '~/components/icon';
import StateDropdown from '../../../state-dropdown';
import DrawerMenuLinks from '../drawer-menu-links';

import { useLoaderContext } from '~/providers/loader-context';
import BettingActions from '~/services/actions/betting-actions';
import TrackingActions, {
  TRACKING_ACTIONS,
  TRACKING_CATEGORIES,
  TRACKING_EVENTS } from
'~/services/actions/tracking-actions';
import ODDS_FORMAT from '~/enums/odds_format';

import INavigationLink from '../../../../../../interfaces/navigation-link';
import useStore from '../../../../../../store';

import styles from './drawer.module.css';
import { useTranslation } from 'react-i18next';

interface IMobleHeaderDrawer {
  open: boolean;
  pages: INavigationLink[];
  toggleDrawer: () => void;
  path: string;
}

interface IMobileHeaderPageStackItem {
  label: string;
  clickEvent: () => void;
}

function Drawer({ open, pages, toggleDrawer, path = '' }: IMobleHeaderDrawer) {
  const { DISABLE_ODDS_FORMAT_SELECTION } = useLoaderContext();
  const { t } = useTranslation(['label']);

  const [oddsFormat, setOddsFormat] = useStore((state) => [
  state.oddsFormat,
  state.setOddsFormat]
  );
  const [pageStack, setPageStack] = React.useState<
    IMobileHeaderPageStackItem[]>(
    []);

  const bettingActions = React.useMemo(() => new BettingActions(), []);
  const trackingActions = React.useMemo(() => new TrackingActions(), []);

  React.useEffect(() => {
    const selectedFormat = bettingActions.getOddsFormat();
    setOddsFormat(selectedFormat);
  }, [open]);

  React.useEffect(() => {
    setPageStack([]);
  }, [path]);

  const topOffset = React.useRef(0);

  // Capture and reset body scroll
  React.useEffect(() => {
    const body = document.querySelector('body');
    if (!body) return;
    if (open) {
      topOffset.current = body?.getBoundingClientRect()?.top;
    } else {
      window.scrollTo(0, Math.abs(topOffset.current));
    }
  }, [open]);

  const handlers = useSwipeable({
    onSwipedLeft: () => toggleDrawer()
    // preventDefaultTouchmoveEvent: true
  });

  const changeOddsFormat = (format: ODDS_FORMAT): void => {
    bettingActions.changeOddsFormat(format);
    window.location.reload();
  };

  const trackMenu = (label: string): void => {
    if (label) {
      trackingActions.eventTracking({
        event: TRACKING_EVENTS.HAMBURGER_MENU_ITEM,
        eventCategory: TRACKING_CATEGORIES.NAVIGATION,
        eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.HAMBURGER_MENU_ITEM],
        eventLabel: label
      });
    }
  };

  const clickBack = () => {
    const [lastPage, ...remainingPages] = pageStack;
    lastPage?.clickEvent?.();
    setPageStack(remainingPages);
  };

  const clickForward = (item: IMobileHeaderPageStackItem) => {
    setPageStack([item, ...pageStack]);
  };

  const renderOddsFormatSelection = () => {
    if (DISABLE_ODDS_FORMAT_SELECTION) return null;

    return (
      <>
				<div className={styles.heading}> {t('RESPONSIVE_ODDS_FORMAT')}</div>
				<div data-testid="odds-format" className={styles.formats}>
					<OCButton
            onClick={() => changeOddsFormat(ODDS_FORMAT.AMERICAN)}
            className={classnames(
              styles.format,
              oddsFormat === ODDS_FORMAT.AMERICAN ? styles.selected : ''
            )}>

						{t('RESPONSIVE_ODDS_FORMAT_AMERICAN')}
						<span className={styles.example}>+125</span>
					</OCButton>

					<OCButton
            onClick={() => changeOddsFormat(ODDS_FORMAT.DECIMAL)}
            className={classnames(
              styles.format,
              oddsFormat === ODDS_FORMAT.DECIMAL ? styles.selected : ''
            )}>

						{t('RESPONSIVE_ODDS_FORMAT_DECIMAL')}
						<span className={styles.example}>2.25</span>
					</OCButton>

					<OCButton
            onClick={() => changeOddsFormat(ODDS_FORMAT.FRACTIONAL)}
            className={classnames(
              styles.format,
              oddsFormat === ODDS_FORMAT.FRACTIONAL ? styles.selected : ''
            )}>

						{t('RESPONSIVE_ODDS_FORMAT_FRACTIONAL')}
						<span className={styles.example}>5/4</span>
					</OCButton>
				</div>
			</>);

  };

  return (
    <div
      data-testid="nav-drawer"
      className={classnames(styles.navDrawer, open ? styles.open : '')}
      {...handlers}
      key={path}>

			<div className={styles.drawerHeader}>
				{!!pageStack?.length &&
        <div className={styles.drawerBackButton} onClick={clickBack}>
						<Icon className={styles.drawerBackArrow} icon="Arrow" />
						{pageStack[0].label}
					</div>
        }
				<OCButton
          className={styles.closeBtn}
          onClick={toggleDrawer}
          testId="close-button">

					<Icon icon="Close" className={styles.closeIcon} />
				</OCButton>
			</div>

			<div className={styles.pages}>
				<DrawerMenuLinks
          pageClick={clickForward}
          trackMenu={trackMenu}
          pages={pages} />

				{renderOddsFormatSelection()}
				<StateDropdown />
			</div>
		</div>);

}

export default Drawer;